import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
} from "recharts"
import { Panel } from "@/components/ui/Panel"
import type { SimTelemetry } from "@/types"

interface ChartPoint {
  t: number
  [key: string]: number | string | null
}

interface SeriesDef {
  points: ChartPoint[]
  color: string
  label: string
}

function seriesToChartData(series: SeriesDef[], key: string): ChartPoint[] {
  const timeSet = new Set<number>()
  series.forEach((s) => s.points.forEach((p) => timeSet.add(p.t)))
  const times = Array.from(timeSet).sort((a, b) => a - b)
  return times.map((t) => {
    const row: ChartPoint = { t }
    series.forEach((s, i) => {
      const pt = s.points.find((p) => p.t === t)
      row[`s${i}`] = pt ? (pt[key] as number) : null
    })
    return row
  })
}

const GRID_COLOR = "#1c2533"
const AXIS_COLOR = "#5b6674"
const TOOLTIP_STYLE = {
  backgroundColor: "#0b1018",
  border: "1px solid #232e3e",
  color: "#e8edf6",
  fontSize: 11,
  borderRadius: 2,
  padding: 6,
}

interface ChartPanelProps {
  title: string
  subtitle?: string
  height?: number
  children: React.ReactNode
}

export function ChartPanel({ title, subtitle, height = 250, children }: ChartPanelProps) {
  return (
    <Panel title={title} padded={false}>
      <div className="p-3">
        {subtitle && <p className="text-[0.625rem] text-dim mb-2">{subtitle}</p>}
        <div style={{ height: `${height}px` }}>{children}</div>
      </div>
    </Panel>
  )
}

function emptyChart(message: string, height = 250) {
  return (
    <div style={{ height: `${height}px` }} className="flex flex-col items-center justify-center text-muted">
      <p className="text-sm">{message}</p>
    </div>
  )
}

function CommonAxes() {
  return (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
      <XAxis
        dataKey="t"
        tick={{ fontSize: 10, fill: AXIS_COLOR }}
        axisLine={false}
        tickMargin={8}
        label={{ value: "Time (s)", position: "insideBottomRight", offset: 4, fontSize: 10, fill: AXIS_COLOR }}
      />
      <YAxis
        tick={{ fontSize: 10, fill: AXIS_COLOR }}
        axisLine={false}
        tickMargin={8}
      />
      <Tooltip
        contentStyle={TOOLTIP_STYLE}
        cursor={{ strokeDasharray: "3 3", stroke: "#334155" }}
      />
      <Legend verticalAlign="top" align="right" height={30} wrapperStyle={{ outline: "none" }} />
    </>
  )
}

export function AltitudeChart({ data, series, height = 250 }: { data?: ChartPoint[]; series?: SeriesDef[]; height?: number }) {
  const pts = series ? seriesToChartData(series, "altitude") : (data || [])
  if (!pts || pts.length === 0) return emptyChart("No altitude telemetry", height)
  return (
    <ChartPanel title="Altitude vs Time" subtitle="Vertical distance from launch pad" height={height}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={pts} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
          {CommonAxes()}
          <Line type="monotone" dataKey="altitude" stroke="#22d3ee" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-2 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-cyan-400" />
        <span className="text-xs text-muted">Altitude (m)</span>
      </div>
    </ChartPanel>
  )
}

export function VelocityChart({ data, series, height = 250 }: { data?: ChartPoint[]; series?: SeriesDef[]; height?: number }) {
  const pts = series ? seriesToChartData(series, "speed") : (data || [])
  if (!pts || pts.length === 0) return emptyChart("No velocity telemetry", height)
  return (
    <ChartPanel title="Velocity vs Time" subtitle="Magnitude of the velocity vector" height={height}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={pts} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
          {CommonAxes()}
          <Line type="monotone" dataKey="speed" stroke="#3b82f6" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-2 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
        <span className="text-xs text-muted">Speed (m/s)</span>
      </div>
    </ChartPanel>
  )
}

export function FuelChart({ data, series, height = 250 }: { data?: ChartPoint[]; series?: SeriesDef[]; height?: number }) {
  const pts = series ? seriesToChartData(series, "fuel") : (data || [])
  if (!pts || pts.length === 0) return emptyChart("No fuel telemetry", height)
  return (
    <ChartPanel title="Fuel vs Time" subtitle="Remaining propellant" height={height}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={pts} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
          {CommonAxes()}
          <Line type="monotone" dataKey="fuel" stroke="#f59e0b" strokeWidth={2} dot={false} />
          <Area type="monotone" dataKey="fuel" fill="#f59e0b" fillOpacity={0.08} />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-2 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="text-xs text-muted">Fuel (kg)</span>
      </div>
    </ChartPanel>
  )
}

export function AccelerationChart({ data, height = 250 }: { data?: ChartPoint[]; height?: number }) {
  const pts = data || []
  if (!pts || pts.length === 0) return emptyChart("No acceleration telemetry", height)
  const accel = pts.map((p, i) => {
    if (i === 0) return { t: p.t, accel: 0 }
    const prev = pts[i - 1]
    const dt = p.t - prev.t
    const dv = (p.speed as number) - (prev.speed as number)
    return { t: p.t, accel: dt ? dv / dt : 0 }
  })
  return (
    <ChartPanel title="Acceleration vs Time" subtitle="Rate of change of velocity" height={height}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={accel} margin={{ top: 16, right: 24, bottom: 26, left: 0 }}>
          {CommonAxes()}
          <Line type="monotone" dataKey="accel" stroke="#a855f7" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-2 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-purple-400" />
        <span className="text-xs text-muted">Acceleration (m/s²)</span>
      </div>
    </ChartPanel>
  )
}

export function SimCharts({ telemetry }: { telemetry: SimTelemetry[] }) {
  if (!telemetry || telemetry.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {["Altitude", "Velocity", "Fuel", "Acceleration"].map((t) => (
          <div
            key={t}
            className="panel p-3 h-[250px] flex flex-col items-center justify-center text-muted text-sm"
          >
            No live telemetry
          </div>
        ))}
      </div>
    )
  }

  const chartData = telemetry.map((p) => ({
    t: p.t,
    altitude: p.altitude,
    speed: p.speed,
    fuel: p.fuel,
  }))

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <AltitudeChart data={chartData} height={250} />
      <VelocityChart data={chartData} height={250} />
      <FuelChart data={chartData} height={250} />
      <AccelerationChart data={chartData} height={250} />
    </div>
  )
}
